import { useEffect, useState } from "react";
import { Button, Form, Row, Spinner } from "react-bootstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Footer from "../../../components/Footer";
import { isErrorResult } from "../../../core/utils/api";
import EntriesFieldArray from "./entriesFieldArray";
import Intervals from "./intervals";
import OutputsFieldArray from "./outputsFieldArray";
import { FleetMateService } from "../../../core/services/FleetMateService";
import { FleetMateConfigs } from "../../../core/interfaces/FleetMate";
import { useAppDispatch, useAppSelector } from "../../../core/redux/hooks";
import { updateAlert } from "../../../core/redux/reducer/alert";
import { getUser } from "../../../core/redux/reducer/auth";
import { useNavigate, useParams } from "react-router-dom";
import { CustomInputGroup, SectionTitle } from "./styles";

const FleetMateGroupSettings: React.FC<{
  id: string | number | null;
  back: () => void;
}> = ({ id = null, back }) => {
  const [errorMessage, setErrorMessage] = useState("");

  // const { id } = useParams();

  const [groupName, setGroupName] = useState("");
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FleetMateConfigs>();

  const onSubmit: SubmitHandler<FleetMateConfigs> = async (data) => {
    const delays: { [key: string]: number } = data.entryPorts
      .filter((port) => Number(port.delay) !== 0)
      .reduce((acc: { [key: string]: number }, port) => {
        acc[`port${port.id}`] = Number(port.delay);
        return acc;
      }, {});

    const entryPorts: { [key: string]: string } = data.entryPorts.reduce(
      (acc: { [key: string]: string }, port: any, index: number) => {
        acc[`port${index + 1}`] = port.sensor.toUpperCase();
        return acc;
      },
      {}
    );

    const exitPorts: { [key: string]: any } = data.exitPorts.reduce(
      (acc: { [key: string]: any }, port: any, index: number) => {
        const { id, name, ...rest } = port;
        acc[`port${index + 1}`] = {
          ...rest,
          pulsed: port.pulsed === "true" || port.pulsed === true,
        };
        return acc;
      },
      {}
    );

    const lorawanNetworkCommunicationIntervals =
      data.lorawanNetworkCommunicationIntervals;

    const iridiumNetworkCommunicationIntervals =
      data.iridiumNetworkCommunicationIntervals;

    const objToSend = {
      group_name: groupName,
      data: {
        lorawanNetworkCommunicationIntervals,
        iridiumNetworkCommunicationIntervals,
        entryPorts,
        delays,
        exitPorts,
      },
    };

    try {
      const result = id
        ? await FleetMateService.updateGroup(id, objToSend)
        : await FleetMateService.storeGroup(objToSend);
      if (!isErrorResult(result) && result) {
        dispatch(updateAlert({ title: t("message.success.save_data") }));
        // navigate("/configs/mate");
        back();
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      console.log("post error", error);
    }
  };

  const onError = (error: any) => console.log("onSubmitError", error);

  async function loadConfigs(id: string | number | null = null) {
    let result = await FleetMateService.getConfigs(id);

    if (
      !result?.data?.entryPorts ||
      !result?.data?.exitPorts ||
      result?.data?.entryPorts?.length === 0 ||
      result?.data?.exitPorts === 0
    ) {
      result = await FleetMateService.getConfigs();
    }

    const data = result?.data;
    setGroupName(result?.data?.name);
    setValue("entryPorts", data?.entryPorts);
    setValue("exitPorts", data?.exitPorts);
    setValue(
      "iridiumNetworkCommunicationIntervals",
      data?.iridiumNetworkCommunicationIntervals
    );
    setValue(
      "lorawanNetworkCommunicationIntervals",
      data?.lorawanNetworkCommunicationIntervals
    );
  }
  async function loadConfigsInfos(id: string | number | null = null) {
    let result = await FleetMateService.getGroupConfigsById(id);

    if (
      !result?.data?.entryPorts ||
      !result?.data?.exitPorts ||
      result?.data?.entryPorts?.length === 0 ||
      result?.data?.exitPorts === 0
    ) {
      result = await FleetMateService.getGroupConfigsById(id);
    }

    if (id) {
      const data = result?.data?.data;

      setGroupName(result?.data?.name);

      data?.entryPorts && setValue("entryPorts", data?.entryPorts);

      data?.exitPorts && setValue("exitPorts", data?.exitPorts);

      data?.iridiumNetworkCommunicationIntervals &&
        setValue(
          "iridiumNetworkCommunicationIntervals",
          data?.iridiumNetworkCommunicationIntervals
        );

      data?.lorawanNetworkCommunicationIntervals &&
        setValue(
          "lorawanNetworkCommunicationIntervals",
          data?.lorawanNetworkCommunicationIntervals
        );
    }
  }

  useEffect(() => {
    loadConfigs();
    if (id) {
      loadConfigsInfos(id);
    }
  }, [id]);

  return (
    <Form
      className="pt-3"
      noValidate
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="mb-3">
        <CustomInputGroup className="mt-2">
          <SectionTitle>{t("configs.fleetmate.ei.setup_name")}</SectionTitle>

          <Form.Control
            className="py-0"
            style={{ height: 36 }}
            placeholder={t("configs.group.name_input")}
            type="text"
            value={groupName}
            onChange={(e) => {
              setGroupName(e?.target?.value);
            }}
          />
        </CustomInputGroup>
      </Row>
      <Intervals control={control} register={register} />
      <EntriesFieldArray control={control} register={register} />
      <OutputsFieldArray control={control} register={register} />

      <Footer>
        <div className="mt-3 mb-3 mx-2">
          {errorMessage && <div className="text-danger">{errorMessage}</div>}
          <Button id="button-submit-fleetmate" type="submit">
            {isSubmitting ? t("status.saving") : t("button.save")}
            {isSubmitting && <Spinner animation="border" size="sm" />}
          </Button>
          <Button
            className="ms-3"
            variant="secondary"
            onClick={() => {
              // navigate("/configs/mate");
              back();
            }}
          >
            {t("button.cancel")}
          </Button>
        </div>
      </Footer>
    </Form>
  );
};

export default FleetMateGroupSettings;
